/**
 * Marketplace applications — pre-baked one-click apps (n8n, etc.) the Instance
 * Create wizard offers as a third OS source alongside distributed + saved images.
 * Each app carries its own images, container list, and a `metadata.fields[]`
 * schema that drives an app-specific config step. Ported from @console
 * `Internal.Opennebula/connectors/marketplace` (`GET /s10003/marketplaces`) +
 * `configuration` (`GET /s10003/images/:id/template`).
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';
import type { ConfigImage } from './configuration';

/** A configurable field on a marketplace app (rendered in the app config step). */
export interface MarketplaceField {
  key: string;
  value?: string;
  helper?: string;
  type?: 'input' | 'select' | string;
  required?: boolean;
  /** Optional grouping heading, e.g. "Mailer Configuration (Optional)". */
  group?: string;
  /** Choices when `type === 'select'`. */
  options?: { name: string; value: string }[];
}

/** A selectable container/component of a marketplace app. */
export interface MarketplaceContainer {
  value: string;
  label: string;
}

/** A marketplace image (same shape as a launch image, plus marketplace fields). */
export interface MarketplaceImage extends ConfigImage {
  uid?: string;
  type?: string;
  version?: string;
}

/** A one-click marketplace application. */
export interface MarketplaceApp {
  name: string;
  distro?: string;
  /** Component template names, e.g. ["n8n","postgresql","nginx"]. */
  template?: string[];
  containers?: MarketplaceContainer[];
  entrypoint?: string;
  uname?: string;
  provisioner?: string;
  images?: MarketplaceImage[];
  healthz?: string;
  metadata?: {
    type?: string;
    fields?: MarketplaceField[];
  };
}

/** Marketplace apps available to launch in this service. */
export function useMarketplaces(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.marketplaces(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const body = unwrap<{ apps?: MarketplaceApp[] }>(
        await serviceApi.get(onPath(serviceId!, 'marketplaces')),
      );
      return body.apps ?? [];
    },
  });
}

/**
 * The OpenNebula template behind a saved image — used to pre-fill the create
 * wizard (default disk size, OS hints) when launching from an existing image.
 */
export function useImageTemplate(serviceId: string | undefined, imageId: string | undefined) {
  return useQuery<Record<string, unknown>>({
    queryKey: ['on', serviceId ?? '', 'images', imageId ?? '', 'template'] as const,
    enabled: !!serviceId && !!imageId,
    retry: false,
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<Record<string, unknown>>(
        await serviceApi.get(onPath(serviceId!, `images/${imageId}/template`)),
      ),
  });
}
